// ─── Consumer Articles ───────────────────────────────────────────────────────
// Static content powering /articles and /articles/:slug — marketplace-intent
// educational pieces (B3 of .claude/plans/content-seo-llm-expansion.md).
// Topics overlap njreap.com's blog by design; the TEXT and the intent do not:
// these are written fresh, framed nationally, and convert toward
// /find-an-appraiser. Never copy content between the two domains.

export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface Article {
  slug: string;
  title: string;
  description: string;
  /** ISO date used for schema.org + display. */
  datePublished: string;
  readMinutes: number;
  intro: string;
  sections: ArticleSection[];
  relatedLinks: { label: string; href: string }[];
}

export const articles: Article[] = [
  {
    slug: 'appraisal-vs-home-inspection',
    title: 'Appraisal vs. Home Inspection: What\'s the Difference?',
    description:
      'Appraisals measure value; inspections measure condition. What each professional does, who they work for, and why a real estate transaction usually needs both.',
    datePublished: '2026-06-12',
    readMinutes: 5,
    intro:
      'Two professionals will probably walk through a home during a purchase — an appraiser and a home inspector — and buyers routinely confuse them. They answer different questions, work for different parties, and protect different interests. Here is the clean distinction.',
    sections: [
      {
        heading: 'The appraiser answers: what is it worth?',
        paragraphs: [
          'An appraiser develops an independent opinion of the property\'s market value. The visit itself is usually brief — measuring the home, photographing rooms, noting quality, condition, and features. The real work happens afterward: researching comparable sales, adjusting for differences, and reconciling the evidence into a documented value conclusion.',
          'In a purchase, the appraisal is ordered for the lender, because the home secures the loan. The appraiser\'s client is the lender even though the buyer typically pays the fee — a structure that exists precisely so the value opinion stays independent of everyone who wants the deal to close.',
        ],
      },
      {
        heading: 'The inspector answers: what condition is it in?',
        paragraphs: [
          'A home inspector works for the buyer, evaluating the condition and operation of the home\'s systems — roof, structure, electrical, plumbing, HVAC, appliances. The deliverable is a condition report flagging defects, safety issues, and items nearing the end of their service life.',
          'Inspectors spend hours where appraisers spend minutes, and they open, test, and probe in ways appraisers do not. But an inspector renders no opinion of value, and a spotless inspection says nothing about whether the price is right.',
        ],
      },
      {
        heading: 'Where the two overlap — and where they don\'t',
        paragraphs: [
          'Both professionals note obvious condition problems, and serious defects affect both reports: the inspector prices the repair conversation, while the appraiser measures how the market discounts the home for it. The difference is purpose. An appraiser cares about condition only insofar as buyers pay for it; an inspector cares about condition for its own sake.',
          'A common and costly misunderstanding: "the appraisal came back fine" does not mean the house is sound, and "the inspection was clean" does not mean the price is fair. Each report is silent on the other\'s question.',
        ],
      },
      {
        heading: 'When you need one without the other',
        paragraphs: [
          'Outside of purchases, appraisals stand alone constantly — estate settlement, divorce, tax appeals, PMI removal, pre-listing pricing. No lender is involved, and the homeowner or attorney engages the appraiser directly, becoming the client themselves.',
          'If you need a defensible number — for a court, the IRS, your servicer, or your own pricing decision — that is appraisal work, and you can request one directly from a licensed appraiser without any transaction in motion.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Find an appraiser', href: '/find-an-appraiser' },
      { label: 'Pre-listing appraisals', href: '/pre-listing-appraisal' },
      { label: 'Glossary: Appraisal', href: '/glossary/appraisal' },
    ],
  },
  {
    slug: 'what-to-expect-during-a-home-appraisal',
    title: 'What to Expect During a Home Appraisal',
    description:
      'How a home appraisal actually works — what the appraiser does on site, how long it takes, what happens afterward, and how to prepare.',
    datePublished: '2026-06-12',
    readMinutes: 6,
    intro:
      'Most homeowners meet an appraiser only a handful of times in their lives, so the process feels opaque: a stranger walks through with a tablet and a laser measurer, and weeks later a number appears. Here is what is actually happening at each step.',
    sections: [
      {
        heading: 'Before the visit',
        paragraphs: [
          'The appraiser researches your property before arriving — public records, prior listings, permits, the neighborhood\'s recent sales. By inspection day they usually know the lot size, the recorded square footage (often wrong, which is partly why they measure), and what the market around you has been doing.',
          'Light preparation helps: make every room accessible including attic and crawlspace hatches, gather a list of improvements with approximate dates and costs, and secure pets. You do not need to deep-clean — appraisers distinguish housekeeping from condition.',
        ],
      },
      {
        heading: 'The on-site inspection',
        paragraphs: [
          'Expect 30 to 60 minutes for a typical home. The appraiser measures the exterior to calculate gross living area — most lender work now requires the ANSI Z765 measurement standard — then walks every interior space, photographing rooms and noting quality of construction, condition, updates, and layout.',
          'They are not testing systems or hunting defects like a home inspector. They are documenting the characteristics the market pays for: finished area, bedroom and bath count, kitchens and baths\' vintage, basements and what portion is finished, garages, and the site itself.',
          'Tell the appraiser about improvements they might not see — a new roof looks like any roof from the ground. A simple dated list (roof 2023, furnace 2021, kitchen 2019) is genuinely useful and entirely appropriate to hand over.',
        ],
      },
      {
        heading: 'After the visit: where the value comes from',
        paragraphs: [
          'The inspection gathers facts; the valuation happens at the desk. The appraiser selects the most comparable recent sales, verifies them, adjusts for differences against your home — site, size, condition, features — and reconciles the adjusted indications into a final opinion, documented in a report that typically runs dozens of pages.',
          'Turnaround for most residential assignments is several days to two weeks after inspection, depending on complexity and how quickly supporting data can be verified.',
        ],
      },
      {
        heading: 'Who gets the report — and what it costs',
        paragraphs: [
          'If a lender ordered the appraisal, the lender is the client; borrowers receive a copy under federal rules, but the appraiser cannot discuss the assignment with you beyond the inspection. If you engage an appraiser directly — estate, divorce, tax appeal, pricing — you are the client, the report is yours, and the appraiser can walk you through it.',
          'Typical single-family fees run a few hundred dollars, scaling with size, complexity, and purpose. Court-ready and IRS-bound work costs more than a simple pricing opinion because the documentation burden is higher.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Find an appraiser', href: '/find-an-appraiser' },
      { label: 'Glossary: Inspection (Appraisal)', href: '/glossary/inspection' },
      { label: 'Glossary: Gross Living Area', href: '/glossary/gross-living-area' },
    ],
  },
  {
    slug: 'when-the-appraisal-comes-in-low',
    title: 'What Happens When the Appraisal Comes in Low',
    description:
      'A low appraisal doesn\'t have to kill the deal. The options for buyers and sellers — reconsideration of value, renegotiation, appraisal gaps — and how to challenge errors the right way.',
    datePublished: '2026-06-12',
    readMinutes: 6,
    intro:
      'The contract says one number, the appraisal says a smaller one, and suddenly the financing math breaks. A low appraisal is stressful but rarely final — and the productive responses are more specific than "fight it."',
    sections: [
      {
        heading: 'Why the loan amount breaks',
        paragraphs: [
          'Lenders lend against the LESSER of price or appraised value. If you agreed to pay $520,000 and the appraisal concludes $500,000, an 80% loan is now 80% of $500,000 — the $16,000 difference comes from somewhere: more cash down, a renegotiated price, or the deal ends.',
          'This is the appraisal doing its job. The lender asked an independent professional whether the collateral supports the loan, and the answer was "not quite at that price."',
        ],
      },
      {
        heading: 'First move: actually read the report',
        paragraphs: [
          'Before reacting, look for concrete problems: wrong square footage, missed renovations, a comparable that was a distressed sale, comparables from a weaker market area when better ones existed. Factual errors and overlooked sales are challengeable; disliking the conclusion is not.',
          'Every lender has a reconsideration of value (ROV) process — recently standardized across the major loan programs — through which the borrower can submit corrections and up to a handful of alternative comparable sales. Specific, factual, and few beats voluminous and indignant: appraisers respond to evidence.',
        ],
      },
      {
        heading: 'The negotiation paths',
        paragraphs: [
          'Most low-appraisal deals survive through one of four routes: the seller reduces the price to the appraised value; the buyer brings additional cash to cover the gap; the parties split the difference; or — where the contract allows — the buyer walks under an appraisal contingency and the house returns to market with a data point its next pricing will have to respect.',
          'Sellers should think hard before dismissing the appraisal as wrong: the next buyer\'s lender will order another one, and the comparable sales will not have changed. An appraisal that lands below contract is often the market telling the listing something.',
        ],
      },
      {
        heading: 'Getting ahead of it',
        paragraphs: [
          'Sellers in unusual homes or fast-moving markets can commission a pre-listing appraisal — pricing on evidence before buyers and their lenders weigh in, with documented support ready if a transaction appraisal later comes in light.',
          'Buyers in competitive markets sometimes waive appraisal contingencies or cap appraisal gaps; both are decisions about exactly the risk this article describes, and both deserve a clear-eyed number rather than optimism.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Pre-listing appraisals', href: '/pre-listing-appraisal' },
      { label: 'Glossary: Loan-to-Value Ratio', href: '/glossary/loan-to-value-ratio' },
      { label: 'Find an appraiser', href: '/find-an-appraiser' },
    ],
  },
  {
    slug: 'how-appraisers-measure-square-footage',
    title: 'How Appraisers Measure a Home — and Why Square Footage Disputes Happen',
    description:
      'ANSI Z765, gross living area, and why the appraiser\'s measurement may differ from tax records and listings. What counts, what doesn\'t, and how to resolve a square-footage dispute.',
    datePublished: '2026-06-12',
    readMinutes: 6,
    intro:
      'Few numbers cause more real estate arguments than square footage. The listing says 2,400, the tax record says 2,150, the appraiser says 2,210 — and each is measuring something different. Here is how professional measurement actually works.',
    sections: [
      {
        heading: 'The standard: ANSI Z765',
        paragraphs: [
          'Since 2022, appraisals for most conventional mortgage work must follow ANSI Z765, the American National Standard for measuring single-family homes. It measures from the exterior, includes only finished, heated areas, and draws bright lines that surprise homeowners.',
          'The headline rules: spaces must have at least a 7-foot ceiling to count (5 feet under sloping rooflines, provided at least half the area reaches 7 feet); areas below grade — even beautifully finished walkout basements — are reported separately from above-grade living area; and finished areas must connect to the house through finished space.',
        ],
      },
      {
        heading: 'Why the records disagree',
        paragraphs: [
          'Tax records are often decades old, drawn from permits or estimates rather than field measurement, and they miss additions and finish work — or include things ANSI excludes. Listings frequently quote tax records, or include finished basements in the headline number where local custom allows.',
          'The result is systematic disagreement: a raised ranch\'s lower level, a Cape Cod\'s half-story under the roofline, a sunroom on a slab — each gets counted differently by different sources. None of the numbers is lying; they are answering differently framed questions. The appraiser\'s ANSI figure is the one a lender\'s valuation will use.',
        ],
      },
      {
        heading: 'Why it matters to value',
        paragraphs: [
          'Buyers compare homes by size, and price-per-square-foot is the market\'s favorite shorthand. A meaningful measurement error compounds across the whole valuation — a 200-square-foot discrepancy at $250 per foot is a $50,000 conversation.',
          'Below-grade area is not worthless — finished basements contribute real value — but markets pay measurably less per foot for it than for above-grade space, which is exactly why the standard separates them.',
        ],
      },
      {
        heading: 'Resolving a dispute',
        paragraphs: [
          'If you believe a measurement is wrong — in an appraisal, a listing, or the tax rolls — the remedy is a professional measurement, not a tape-measure debate. Appraisers offer measurement-only assignments: an ANSI-standard field measurement with a floor plan and a gross living area statement, usable to correct records, support a listing, or challenge another report.',
          'Sellers of homes where records understate the true finished area have the most to gain: documenting the real number before listing prevents both underpricing and the mid-contract renegotiation that follows a buyer-side surprise.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Pre-listing appraisals', href: '/pre-listing-appraisal' },
      { label: 'Glossary: Gross Living Area', href: '/glossary/gross-living-area' },
      { label: 'Find an appraiser', href: '/find-an-appraiser' },
    ],
  },
];

export const articlesBySlug = new Map(articles.map((a) => [a.slug, a]));
